import { useCallback, useEffect, useRef, useState } from 'react'

import { instantOf } from './freshness.js'
import { DEPLOYMENT_FAULT_STATUS, comparePosition, joinableId, shortEasternDate } from './watch.js'
import { READY as ARCHIVE_READY, fetchArchive, titlesOf } from './watchArchive.js'

// The title page's own data — the fetch, and everything derived from its
// answer that is not already exported by `watch.js`. W3b (P6-244).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND THE PAGE MUST RENDER WITHOUT IT — `watch.js`'s OWN
//    RULE, UNCHANGED
// ═══════════════════════════════════════════════════════════════════════════
//
// Offline, 401, 404, 500, 502, 503, a body that is not JSON — every one of
// them resolves to a state the component renders a sentence for, and no
// caller needs a `catch`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 FOUR THINGS THAT MUST NOT READ THE SAME — THE BRIEF'S OWN LIST
// ═══════════════════════════════════════════════════════════════════════════
//
// A title that does not exist (`NOT_FOUND`, a 404 the Function only sends
// once it has actually looked in the summary's own `subscriptions[]`), a
// detail object not published yet, an unreachable bucket, and a title whose
// seasons have not been fetched yet. The middle two travel over the wire as
// `detailFailure` — see `functions/api/watch/titles/[subscriptionId].js` and
// `functions/_lib/projections.js::TITLE_DETAIL_FAILURE` — because the REST of
// the page (hero, standing, next-up, providers, who's in) can render in full
// from `subscription` alone even when the seasons column cannot. The fourth
// is not a fetch failure at all: `seasonsNotFetchedYet` below reads it out of
// a perfectly healthy 200.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A FIFTH, ADDED IN V2-2b ROUND 2 (manager review, "no dead ends") —
//    `ARCHIVED`, NOT THE SAME AS `NOT_FOUND`
// ═══════════════════════════════════════════════════════════════════════════
//
// The archive (`site/src/portal/WatchArchive.jsx`) links every title it
// lists to this exact route, including titles off the watchlist — and a
// removed title is exactly what a member browsing the archive will tap. A
// bare `NOT_FOUND` there would be a dead end: "the archive says this
// happened, and the one link about it says it doesn't exist." So a 404 from
// the titles endpoint is no longer automatically `NOT_FOUND` — it triggers
// one more read, of the archive itself (`watchArchive.js::fetchArchive`,
// reused verbatim, never a second implementation of that fetch), and ONLY
// if the id is not there EITHER does this settle on `NOT_FOUND`. A title in
// NEITHER source reads exactly as before this round.

export const TITLE_ENDPOINT = (subscriptionId) => `/api/watch/titles/${subscriptionId}`

export const LOADING = 'loading'
export const READY = 'ready'
export const NOT_FOUND = 'not_found'
export const UNAVAILABLE = 'unavailable'
export const MISCONFIGURED = 'misconfigured'
export const ARCHIVED = 'archived'
// P6-318: there is no state for "a season of a listed show that is not on the
// list" any more -- a show is one unit, so every one of its seasons is listed
// and gets the ordinary `READY` page. (P6-298b's `SEASON` state, and the
// sentence its page carried, were removed with the thing they described.)

export function loadingTitle() {
  return { state: LOADING }
}

export function notFoundTitle() {
  return { state: NOT_FOUND }
}

export function unavailableTitle() {
  return { state: UNAVAILABLE }
}

export function misconfiguredTitle() {
  return { state: MISCONFIGURED }
}

/** A title off the watchlist, still in the archive — round 2's "no dead
 * ends." `archiveTitle` is one row of `watchArchive.js::titlesOf`'s own
 * array (`{id, kind, title, poster_url, year, listed, members}`) — the
 * IDENTICAL shape the archive page itself already renders, never a second
 * one invented for this view. */
export function archivedTitle(archiveTitle) {
  return { state: ARCHIVED, archiveTitle }
}

/** `detail`/`detailFailure`/`you` — the three fields the endpoint's answer
 * carries beside `subscription`. */
function sharedTitleFields(payload) {
  const detail =
    payload.detail && typeof payload.detail === 'object' && !Array.isArray(payload.detail)
      ? payload.detail
      : null
  const detailFailure = typeof payload.detailFailure === 'string' ? payload.detailFailure : null
  const you = typeof payload.you === 'string' ? payload.you : null
  return { detail, detailFailure, you }
}

/** A payload's own `series` field, defensively — `null` for anything that is
 * not a plain object (absent entirely in the deploy-gap window before the
 * bot's first publish of P6-298a's `series` map, or simply not yet resolved
 * for a series-of-one). */
function seriesFieldOf(payload) {
  const series = payload.series
  return series && typeof series === 'object' && !Array.isArray(series) ? series : null
}

/**
 * The Function's answer, checked at the seam — `watch.js::buildWatch`'s exact
 * discipline, applied to this endpoint's own envelope.
 *
 * `you` is a string (the caller's own Discord id, per
 * `functions/api/watch/titles/[subscriptionId].js`'s header on why THIS
 * endpoint returns it rather than making the page call `useSession()` a third
 * time) or `null` if the wire ever disagrees — never coerced.
 *
 * One successful shape: a LISTED title answers with `subscription` (`READY`).
 * Anything shaped like that -- the payload itself malformed, or carrying no
 * `subscription` as a real object -- is `UNAVAILABLE`.
 */
export function buildTitle(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return unavailableTitle()

  const subscription = payload.subscription
  if (subscription && typeof subscription === 'object' && !Array.isArray(subscription)) {
    return { state: READY, subscription, series: seriesFieldOf(payload), ...sharedTitleFields(payload) }
  }

  return unavailableTitle()
}

export async function fetchTitle(subscriptionId, fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableTitle()
  if (typeof subscriptionId !== 'string' || subscriptionId === '') return unavailableTitle()
  try {
    const response = await fetchImpl(TITLE_ENDPOINT(subscriptionId), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      if (response.status === 404) return fetchArchivedFallback(subscriptionId, fetchImpl)
      return response.status === DEPLOYMENT_FAULT_STATUS ? misconfiguredTitle() : unavailableTitle()
    }
    return buildTitle(await response.json())
  } catch {
    return unavailableTitle()
  }
}

/**
 * The "no dead ends" fallback (round 2): a title the watchlist summary does
 * not know is not necessarily one nobody has ever seen. Reads the SAME
 * archive fetch the archive page itself uses; if the id is not there
 * either, or the archive itself cannot be read right now, this settles on
 * the ordinary not-found sentence rather than inventing a THIRD failure
 * mode for "we could not tell." A signed-out caller already never reaches
 * here (a 401 is not a 404, so it never enters this branch at all) — this
 * fallback fetch is reached only once the FIRST request has already
 * established there is somebody signed in to ask.
 */
async function fetchArchivedFallback(subscriptionId, fetchImpl) {
  const archive = await fetchArchive(fetchImpl)
  if (archive.state === ARCHIVE_READY) {
    const match = titlesOf(archive.shared).find((title) => title.id === subscriptionId)
    if (match) return archivedTitle(match)
  }
  return notFoundTitle()
}

/**
 * One title, for one page load. A single fetch on mount, re-run only if the
 * URL's own id changes (a member navigating from one title page straight to
 * another via the back/forward stack, or a future "related title" link) —
 * `movie.js::useMovie`'s shape, not `music.js::useMusic`'s poll, for the
 * reason `watch.js::useWatch` already gives: nothing behind this projection
 * moves on a two-second timescale.
 */
export function useWatchTitle(subscriptionId) {
  const [title, setTitle] = useState(loadingTitle)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // 🔴 V2-3b's own addition: `refresh()`, the same on-demand escape hatch
  // `watch.js::useWatch` gained for the identical reason (ADR-0009 D3 — the
  // bot publishes right after applying a portal request). It does NOT reset
  // to `loadingTitle()` -- only the effect below does that, gated on the id
  // actually changing -- so a button-triggered refresh never flashes
  // "Loading the title…" over a page that was already rendering.
  const refresh = useCallback(() => {
    fetchTitle(subscriptionId).then((answer) => {
      if (mounted.current) setTitle(answer)
    })
  }, [subscriptionId])

  // 🔴 P6-281 §1.1's own addition: `confirm(isConfirmed)`, `watch.js::
  // useWatch`'s identical bounded re-read loop, applied to THIS title's own
  // endpoint. See that hook's own header for why it exists (the applier now
  // publishes before it reports "applied", so this is the defense for
  // whatever propagation lag remains beyond that fix, never the primary
  // mechanism).
  //
  // ⚠️ `isConfirmed` is called with a `{subscriptions: [...]}` wrapper
  // around the ONE title this endpoint returns, never the bare subscription
  // — `ProgressStepper`/`MemberActionRow` (`WatchPanel.jsx`) are SHARED
  // between the panel (whose own `confirm` hands `isConfirmed` a real
  // `shared` blob with many subscriptions) and this page, so both callers
  // must present the identical shape or the shared component would need to
  // guess which one it was given. `watch.js::subscriptionsOf` reads only
  // `shared.subscriptions`, so this one-element wrapper is sufficient for
  // it to find the (one) matching row exactly the way the panel's own
  // multi-title list does.
  const confirm = useCallback(
    async (isConfirmed) => {
      for (let attempt = 0; attempt < CONFIRM_TRIES; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop -- each attempt must
        // see the PREVIOUS one's answer before deciding whether to retry.
        const answer = await fetchTitle(subscriptionId)
        if (!mounted.current) return false
        setTitle(answer)
        if (answer.state === READY && isConfirmed({ subscriptions: [answer.subscription] })) {
          return true
        }
        if (attempt < CONFIRM_TRIES - 1) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(CONFIRM_INTERVAL_MS)
          if (!mounted.current) return false
        }
      }
      return false
    },
    [subscriptionId],
  )

  useEffect(() => {
    setTitle(loadingTitle())
    refresh()
  }, [refresh])

  return { ...title, refresh, confirm }
}

/** `watch.js::useWatch`'s own bound, mirrored -- see that module's own
 * comment for why it is kept small. */
const CONFIRM_TRIES = 5
const CONFIRM_INTERVAL_MS = 600

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/** One slow clock for the whole page — `PortalHome.jsx::useNow`'s exact
 * shape, duplicated rather than shared because this is a STANDALONE route
 * with no portal page above it to own a single instance and pass down as a
 * prop; there is no cross-route clock-sharing mechanism in this codebase and
 * building one is out of scope for one page's countdown and staleness
 * checks. A minute is far finer than anything this page reconciles on. */
export function useNow() {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [])
  return now
}

// ═══════════════════════════════════════════════════════════════════════════
// SEASONS AND EPISODES — the left column
// ═══════════════════════════════════════════════════════════════════════════

/** `seasons[]`, defensively — the detail object's own array, never `null`. */
export function seasonsOf(detail) {
  const list = detail?.seasons
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

/**
 * Has the backfill even started for this title? Brief's fourth failure
 * state, and W3a §5/§9's own words: "a brand-new title may have no seasons
 * yet... render it as not checked yet, never as empty-and-broken."
 *
 * ⚠️ MOVIE EXCLUDED, DELIBERATELY. `seasons: []` is a film's PERMANENT,
 * correct answer (W3a §3: "a film has no episodes. Empty, not
 * absent-and-broken") — never "not checked yet". Telling the two apart is
 * this function's entire job; collapsing them would print "waiting for the
 * first check" under every film's title page forever.
 */
export function seasonsNotFetchedYet(subscription, detail) {
  if (subscription?.kind === 'movie') return false
  return detail !== null && seasonsOf(detail).length === 0
}

/**
 * `Season 4`, or `Specials` for TMDB's season 0 — THE PRESENTATION CALL the
 * brief asks for, decided here rather than left to render as `Season 0`.
 *
 * 🔴 FINDING (report §…): the handoff never mentions specials, and W3a's own
 * decision was to publish season 0 unfiltered if TMDB reports one — "this is
 * your presentation call." Season 0 is not filtered here either: it gets a
 * chip like any other season, at its own place in the already-sorted list
 * (W3a §9: "a populated, already-sorted-by-number list" — this file does not
 * re-sort it, which would risk moving it away from where TMDB actually put
 * it). The ONLY change is the label: TMDB's own UI and documentation call
 * season 0 "Specials", and a member reading "Season 0" beside "Season 1"
 * would reasonably read it as a data error rather than the bonus-content
 * bucket it actually is.
 *
 * An AniList entry's `number` is always `null` (no season concept at all —
 * matches `episodeCode`'s own convention throughout `watch.js`) and reads
 * `Episodes`, never `Season null` or a fabricated `Season 1`.
 */
export function seasonLabel(number) {
  if (number === 0) return 'Specials'
  return Number.isInteger(number) ? `Season ${number}` : 'Episodes'
}

/** `episodes[]` for one season entry, or `null` — the three-state contract
 * W3a §9 hands down verbatim: `null` ("not fetched"), `[]` ("genuinely no
 * episodes"), or a populated, sorted list. Never collapsed into one another. */
export function episodesOf(season) {
  const list = season?.episodes
  if (list === null || list === undefined) return null
  return Array.isArray(list) ? list.filter((row) => row && typeof row === 'object') : []
}

// V2-8b: `comparePosition` MOVED to `watch.js` — the watchlist row's own
// per-season track needs the identical comparison, and this module already
// imports FROM `watch.js`, so importing it back the other way would be a
// circular import between the two. Imported above; every call site below is
// unchanged.

/**
 * One episode's state mark — Handoff §5: "`SEEN TOGETHER` below the group
 * marker, `NOT YET` for an aired episode the group has not reached,
 * `UNAIRED` above it."
 *
 * Aired-ness is read off `subscription.last_aired` — the bot's own diffed
 * fact, never date arithmetic against `air_date`. `toWatchInfo` in
 * `watch.js` already treats `last_aired` as the authority on "what has
 * aired"; a second derivation here from `air_date` vs. wall-clock would be
 * CLAUDE.md gotcha 1's *copies* axis — two implementations of one fact,
 * free to disagree the day a provider's date and the bot's own diff drift by
 * a day (a timezone, a delayed episode, a provider correction).
 *
 * `unaired` when nothing has aired at this position yet, whatever `progress`
 * says (nothing to have watched). Otherwise `seen` when the GROUP's position
 * is strictly past this episode, `notYet` when it is not (this includes the
 * episode `progress` itself points at, and anything aired beyond it — the
 * group can be more than one episode behind).
 */
export function episodeState(season, episode, subscription) {
  const lastAired = subscription?.last_aired
  const airedCmp = Number.isInteger(lastAired?.episode)
    ? comparePosition({ season, episode }, { season: lastAired.season ?? null, episode: lastAired.episode })
    : null
  const aired = airedCmp !== null && airedCmp <= 0
  if (!aired) return 'unaired'

  const progress = subscription?.progress
  const groupCmp = Number.isInteger(progress?.episode)
    ? comparePosition({ season, episode }, { season: progress.season ?? null, episode: progress.episode })
    : null
  // No progress recorded at all: the group has not started, so every aired
  // episode is still "not yet" for them.
  return groupCmp !== null && groupCmp < 0 ? 'seen' : 'notYet'
}

/** Does the group's own position land on this exact row? Handoff: "placed
 * immediately above the episode they are up to... the marker only appears in
 * the season the group is in." */
export function isGroupPosition(season, episode, subscription) {
  const progress = subscription?.progress
  if (!Number.isInteger(progress?.episode)) return false
  return comparePosition({ season, episode }, { season: progress.season ?? null, episode: progress.episode }) === 0
}

/**
 * Does the SIGNED-IN MEMBER'S OWN position land on this exact row? P6-283's
 * sibling to `isGroupPosition` immediately above — same comparison, a
 * different position. Takes `yourProgress` directly (`deriveYourProgress`,
 * `watch.js`), never `subscription`, for the identical reason
 * `nextEpisodeToOffer`/`catchUpTarget` do: `subscription.progress` is the
 * GROUP's derived position, and conflating the two here would put "you're
 * here" on the group's row for a member who has never marked one of their
 * own.
 */
export function isYourPosition(season, episode, yourProgress) {
  if (!Number.isInteger(yourProgress?.episode)) return false
  return (
    comparePosition({ season, episode }, { season: yourProgress.season ?? null, episode: yourProgress.episode }) === 0
  )
}

/**
 * V2-6g2: above this many episodes in one season, `SeasonBody` shows a
 * window rather than every row at once. One Piece's ~1,179 aired episodes
 * (brief §0) is the shape this exists for; comfortably above the longest
 * short season this site already renders in full (an ordinary TMDB season
 * rarely exceeds the mid-20s, and the brief's own "must not change" case is
 * 12 or 24), so nothing that shows every row today starts collapsing.
 */
export const EPISODE_COLLAPSE_THRESHOLD = 60

/** Rows either side of a marked position that stay visible in a collapsed
 * list. */
const COLLAPSE_CONTEXT_RADIUS = 2

/** The most-recently-aired rows that stay visible in a collapsed list,
 * regardless of anyone's position — "the latest aired few" (brief §2). */
const COLLAPSE_LATEST_AIRED_COUNT = 5

/**
 * Which indices into `episodes` a collapsed season body should render.
 * Never called at or under `EPISODE_COLLAPSE_THRESHOLD` — `SeasonBody` only
 * reaches for this once the season's own list is long enough to need it.
 *
 * Three anchors, unioned rather than chosen between (a member can be far
 * behind both the group and the tail, and the group can be far from the
 * tail too):
 *
 * - the member's own position, read through `isYourPosition` — the EXACT
 *   predicate the "You're here" marker itself uses, never a second
 *   derivation of "where are you" (gotcha 21: a guard and what it guards
 *   must agree by sharing one implementation, not by two that can drift);
 * - the group's own position, through `isGroupPosition`, for the same
 *   reason the marker above it uses that function;
 * - the `COLLAPSE_LATEST_AIRED_COUNT` most recently aired rows, through
 *   `episodeState`, the identical function every row already reads its own
 *   aired-ness from.
 *
 * Returns a sorted, deduplicated array of ARRAY INDICES, never episode
 * numbers — an index is what `episodes[i]` and `episodeRuns` both need, and
 * a season's own episode numbers are not guaranteed to start at 1 or run
 * without gaps.
 */
export function collapsedEpisodeIndices(episodes, seasonNumber, subscription, yourProgress) {
  const visible = new Set()

  const addWindow = (index) => {
    if (index === -1) return
    const from = Math.max(0, index - COLLAPSE_CONTEXT_RADIUS)
    const to = Math.min(episodes.length - 1, index + COLLAPSE_CONTEXT_RADIUS)
    for (let i = from; i <= to; i += 1) visible.add(i)
  }
  addWindow(episodes.findIndex((episode) => isYourPosition(seasonNumber, episode.number, yourProgress)))
  addWindow(episodes.findIndex((episode) => isGroupPosition(seasonNumber, episode.number, subscription)))

  let remaining = COLLAPSE_LATEST_AIRED_COUNT
  for (let i = episodes.length - 1; i >= 0 && remaining > 0; i -= 1) {
    if (episodeState(seasonNumber, episodes[i].number, subscription) === 'unaired') continue
    visible.add(i)
    remaining -= 1
  }

  return Array.from(visible).sort((a, b) => a - b)
}

/**
 * Collapses a sorted, deduplicated array of indices (`collapsedEpisodeIndices`)
 * into contiguous `[start, end]` runs, so `SeasonBody` can render each run in
 * order and place one plain gap between two runs that are not adjacent.
 * "The list keeps its order" (brief §2) holds inside a run and across a gap
 * alike — nothing here ever reorders an index, only groups them.
 */
export function episodeRuns(indices) {
  const runs = []
  let start = null
  let prev = null
  for (const index of indices) {
    if (start === null) {
      start = index
    } else if (index !== prev + 1) {
      runs.push([start, prev])
      start = index
    }
    prev = index
  }
  if (start !== null) runs.push([start, prev])
  return runs
}

/**
 * V2-6g2 round 2 — `GroupProgressBlock`'s own collapse (`WatchTitle.jsx`,
 * "Where everyone is"). Live risk: V2-6g already made a >100-episode anime
 * possible tonight, and this section drew one 2px block per episode with no
 * cap — `.d2d-watchtitle__progressblock`'s own `min-width: 2px` floor times
 * ~1,179 episodes is ~2,358px, overflowing every width. Reuses this file's
 * own threshold and window constants (`EPISODE_COLLAPSE_THRESHOLD`,
 * `COLLAPSE_CONTEXT_RADIUS`, `COLLAPSE_LATEST_AIRED_COUNT`) — a season short
 * enough for `SeasonBody`'s own view is short enough for this one too.
 *
 * Unlike `collapsedEpisodeIndices` (built for exactly two positions, "you"
 * and "the group"), this section draws ONE ROW PER MEMBER, and every
 * member's own position must stay visible and exact — never rounded to a
 * neighbouring episode, brief's own rule. So this windows around EVERY
 * position in `positions` (any length, in any order; an entry with no
 * integer episode contributes nothing, same as a member with no position at
 * all), unioned with the latest-aired tail. `GroupProgressBlock` computes
 * this ONCE and hands the same runs to every member's own row, so a given
 * horizontal position means the same episode everywhere — the whole point
 * of the section is comparing members against each other.
 *
 * Returns a sorted, deduplicated array of ARRAY INDICES, exactly like
 * `collapsedEpisodeIndices` — never episode numbers, for the identical
 * reason (an index is what `episodes[i]` and `episodeRuns` both need).
 */
export function groupProgressWindowIndices(episodes, seasonNumber, subscription, positions) {
  const visible = new Set()

  const addWindow = (index) => {
    if (index === -1) return
    const from = Math.max(0, index - COLLAPSE_CONTEXT_RADIUS)
    const to = Math.min(episodes.length - 1, index + COLLAPSE_CONTEXT_RADIUS)
    for (let i = from; i <= to; i += 1) visible.add(i)
  }
  for (const position of positions) {
    if (!Number.isInteger(position?.episode)) continue
    addWindow(
      episodes.findIndex(
        (episode) =>
          comparePosition(
            { season: seasonNumber, episode: episode.number },
            { season: position.season ?? null, episode: position.episode },
          ) === 0,
      ),
    )
  }

  let remaining = COLLAPSE_LATEST_AIRED_COUNT
  for (let i = episodes.length - 1; i >= 0 && remaining > 0; i -= 1) {
    if (episodeState(seasonNumber, episodes[i].number, subscription) === 'unaired') continue
    visible.add(i)
    remaining -= 1
  }

  return Array.from(visible).sort((a, b) => a - b)
}

// V2-8b: `groupLeaderIds` MOVED to `watch.js` for the identical reason
// `comparePosition` above did — the row's own You box needs the same
// "who is the sole leader" question `yourStanding` (`watch.js`) asks.

/** The shared "is this air_date actually known" primitive. `episodeDateLabel`
 * and `seasonHasUnknownAirDate` below both read through this ONE function
 * rather than each parsing `air_date` its own way, so a row's own date cell
 * and the list's honesty note can never disagree about which episodes count
 * as dateless — CLAUDE.md gotcha 21: a guard and the thing it guards must
 * share one derivation of a fact, never two that can quietly drift apart. */
function knownAirDateMs(airDate) {
  return typeof airDate === 'string' ? instantOf(airDate) : null
}

/** `14 Aug`, or the honest absence — an episode's `air_date` can be `null`
 * even inside a populated episode list (W3a: TMDB does not always have a
 * date for a far-future, unannounced episode; V2-6g: AniList's own airing
 * history is often incomplete for an older or long-running anime). Lower-case
 * source text, same reasoning `watch.js`'s own date helpers give: the
 * `d2d-micro` class upper-cases it for display; baking the case in here would
 * fight that while shouting at anyone reading the raw string (a screen
 * reader, a test). */
export function episodeDateLabel(airDate) {
  const ms = knownAirDateMs(airDate)
  return ms === null ? 'date unknown' : shortEasternDate(ms)
}

/**
 * True when at least one episode in this list has no known air date.
 *
 * V2-6g2's replacement for the old, anime-only note that claimed AniList
 * "publishes only the most recently aired episode and the next one" — false
 * since V2-6g: a FINISHED or long-running anime's list can be the title's
 * full episode count (Slime S1: 24 rows, 1 dated). The truth generalises
 * past anime: whatever season is on screen, if any of ITS episodes lack a
 * real date, the member is told so once, plainly, rather than left to notice
 * one blank-looking row at a time.
 *
 * Always called with the season's FULL episode list, never a collapsed
 * view's own visible slice — so a dateless row hidden behind "Show all N
 * episodes" still gets the note it would otherwise silently lack.
 */
export function seasonHasUnknownAirDate(episodes) {
  return episodes.some((episode) => knownAirDateMs(episode?.air_date) === null)
}

/** A stable key for the per-episode "revealed" set — never persisted (client
 * state only, per-page-view, per Handoff's own rule for the reveal). */
export function episodeRevealKey(season, episode) {
  return `${season ?? 'x'}:${episode}`
}

/**
 * The 3a `threads` object's own key shape for one episode — the exact
 * inverse of `watch.js::episodeCodeFromThreadKey`, which parses this same
 * format back out. `cogs/watch/refresh.py::_thread_key` is the one writer;
 * this is the one other reader of its convention, kept beside the function
 * that already reads it back rather than re-derived at each call site.
 */
export function threadKeyFor(season, episode) {
  return Number.isInteger(season) ? `${season}x${episode}` : String(episode)
}

/** The thread URL for one episode, or `null`. */
export function threadUrlFor(subscription, season, episode) {
  const threads = subscription?.threads
  if (!threads || typeof threads !== 'object') return null
  const url = threads[threadKeyFor(season, episode)]
  return typeof url === 'string' ? url : null
}

// ═══════════════════════════════════════════════════════════════════════════
// THE "WE'RE HERE" CONTROL — copy-the-command, and nothing here writes
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The exact command text the control offers — Handoff §5: "the episode
 * offered is `last_aired`, NOT `progress + 1`... the member can edit the
 * numbers before sending." Built from `last_aired`, never from `progress`.
 *
 * `null` when nothing has aired yet (no `last_aired` at all) — there is no
 * honest episode to offer, and the control renders its own explanation
 * rather than a command naming an episode that does not exist. The handoff
 * does not draw this state explicitly (its worked example already has
 * `last_aired`); it is Handoff Finding-shaped rather than a silent gap — see
 * the report.
 *
 * 🔴 ANIME'S SEASON SLOT IS `0`, A DELIBERATE PLACEHOLDER. `/watch progress`
 * requires a season argument for every kind (`cogs/watch/cog.py`'s own
 * comment: "still asked for every kind" — the LOOKUP ignores it for anime,
 * the SIGNATURE does not), and AniList has no season number to offer
 * (`last_aired.season` is always `null` for an anime title). `0` is the
 * command's own documented minimum (`Range[int, 0, 999]`) and reads to a
 * human as "not applicable" rather than a guessed real season — and the
 * member can edit it, which is the whole design of this control.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 🔴 P6-275 — NAMED PARAMETERS, NOT POSITIONAL. THIS WAS THE WHOLE PURPOSE
 *    OF THE CONTROL AND IT DID NOT WORK.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * The command used to read `/watch progress Ted Lasso 4 7` — positional.
 * Discord slash commands parse NAMED parameters (`title:`, `season:`,
 * `episode:`); Jordan measured the positional form does not paste at all.
 * The names below are copied from `cogs/watch/cog.py::watch_progress`'s
 * real signature (`title`, `season`, `episode` — no `@app_commands.rename`
 * anywhere on that command), not invented here: a wrong name is a command
 * that silently fails to parse, so this is a derived fact, not a guess.
 *
 * `season:` and `episode:` are also what makes free-text `title` safe.
 * The title can itself contain the word "Season" and a digit (a real one:
 * "That Time I Got Reincarnated as a Slime Season 4") — Discord's parser
 * finds the LAST `season:`/`episode:` tokens, not the first digit, so the
 * title's own words never get mistaken for the real parameters.
 */
export function progressCommand(subscription) {
  const lastAired = subscription?.last_aired
  if (!Number.isInteger(lastAired?.episode)) return null
  const title = typeof subscription?.title === 'string' ? subscription.title : ''
  const season = Number.isInteger(lastAired.season) ? lastAired.season : 0
  return `/watch progress title:${title} season:${season} episode:${lastAired.episode}`
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-6h2 — "LINK THE NEXT SEASON": THE SELF-REFERENCE GUARD
// ═══════════════════════════════════════════════════════════════════════════
//
// The brief's own words: "Never offer the title itself (the same provider
// and id)." 🔴 **ROUND 2 (manager review): the FIRST cut compared title text,
// and that is the wrong direction to fail in.** `record["source"]` (a
// subscription's own `{provider, id}`) was not published on the wire when
// this slice first shipped, so it compared titles instead — and a text
// comparison is a FALSE NEGATIVE risk, not merely an imprecise one: a
// same-titled but genuinely different work (a remake — Fruits Basket
// 2001/2019, Hunter x Hunter 1999/2011) would have been hidden from this
// picker FOREVER, with no way to link it from this page at all. That is the
// dangerous direction for a client-side proxy sitting in front of a server
// that already refuses the true self-reference case — a false NEGATIVE here
// blocks a legitimate action, where a false positive only ever costs a
// clean, honest refusal (`_link_sequel_is_self_reference`,
// `cogs/watch/requests.py`, "A title can't be its own next season.").
//
// ✅ **The wire gap has since closed** — V2-6f2's own round 2 (another lane)
// publishes `record["source"]` (`{provider, id}`) on `_subscription_view`,
// `_title_detail_view` and `_archive_title_view`
// (`utils/watch_projection.py`, not touched by this slice — that lane owns
// it). This function now compares by SOURCE ID, never text:
//
// - `hit.provider === subscription.source.provider` AND the ids are equal
//   (compared as strings — a wire id may be a number, `source.id` may be
//   either, and this never assumes which);
// - for TMDB specifically, ALSO `hit.kind === subscription.kind` — TMDB's
//   own id space collides between `tv` and `movie` (measured precedent:
//   `logTargetForSearchHit`'s own docstring), so id equality alone would
//   wrongly treat an unrelated movie sharing a TV show's numeric id as the
//   title itself. AniList has one numbering space, so provider+id alone is
//   enough there.
// - `subscription.source` absent (not yet deployed everywhere, or an older
//   cached projection): excludes NOTHING — the same fail-open reasoning as
//   the text-proxy it replaces, now grounded in an id rather than a guess.
export function isSelfReferenceHit(hit, subscription) {
  const source = subscription?.source
  if (!source || typeof source !== 'object') return false
  if (typeof hit?.provider !== 'string' || hit.provider !== source.provider) return false
  if (String(hit.id) !== String(source.id)) return false
  return hit.provider !== 'tmdb' || hit.kind === subscription?.kind
}
